/**
 * Payroll Review - list page.
 *
 * Shows every Xero-connected client. Clicking a row drills into the
 * detail page where the firm sees each employee's current wage vs the
 * Fair Work minimum, missing fields the client should populate, and the
 * relevant pay-guide PDF.
 *
 * Designed for the 1-July audit workflow - when Fair Work publishes new
 * minimum wages each year, accountants must check every client.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Users, Briefcase, ExternalLink, AlertCircle, Loader2 } from 'lucide-react';
import { listPayrollClients, PayrollClientSummary } from '@/services/api/payroll';

export default function PayrollReviewPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['payroll-clients'],
    queryFn: listPayrollClients,
    staleTime: 60_000,
  });

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <header className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-emerald-600" />
          Payroll Review
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Check each client's employees against the Fair Work minimum wage. Designed for the 1 July annual update - click into a client to see per-employee wage comparisons.
        </p>
      </header>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading clients…
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-md p-4 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <div>
            <p className="font-medium">Couldn't load payroll clients.</p>
            <p className="text-xs mt-1">{(error as Error)?.message}</p>
          </div>
        </div>
      )}

      {data?.clients && data.clients.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-6 text-center">
          <p className="font-medium mb-1">No connected clients.</p>
          <p className="text-sm">Connect a client to accounting software from the Clients page to see them here.</p>
        </div>
      )}

      {data?.clients && data.clients.length > 0 && (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase font-semibold tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left">Client</th>
                <th className="px-4 py-2.5 text-left">Industry</th>
                <th className="px-4 py-2.5 text-left">Accounting org</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map((c: PayrollClientSummary) => (
                <tr
                  key={c.clientId}
                  className="border-t hover:bg-muted/30 cursor-pointer"
                  onClick={() => navigate(`/panel/payroll/${c.clientId}`)}
                >
                  <td className="px-4 py-3 font-medium flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-muted-foreground" />
                    {c.displayName}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.industry || '-'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.xeroOrgName || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                      Review <ExternalLink className="w-3 h-3" />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-6 text-[11px] text-muted-foreground italic">
        Wage comparisons use AI to read Fair Work pay guide PDFs - always verify before processing payroll. Data refreshes every 30 minutes per client.
      </p>
    </div>
  );
}
